import { fontFamilyDefinition } from "client/Misc/fontTools";
import { FontFamily } from "client/Api/common";
import { isNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { Mark, Schema } from "prosemirror-model";
import {
	EditorState,
	Plugin,
	PluginKey,
	Selection,
	Transaction
} from "prosemirror-state";
import { ColorMarkAttributes } from "../schema/marks/color";
import {
	FontFamilyKind,
	FontFamilyMarkAttributes,
	tryResolvePredefinedFontFamily
} from "../schema/marks/fontFamily";
import {
	FontSizeKind,
	FontSizeMarkAttributes,
	isPredefinedFontSize
} from "../schema/marks/fontSize";
import {
	emptyDefaultFontMarks,
	isDefaultFontMarksNodeAttributes,
	SupportedDefaultFontMarks
} from "../schema/nodes/attributes/defaultFontMarks";
import { hasMarks, RichTextEditorMarks } from "../schema";

export const defaultFontMarksPlugin: Plugin<null> = new Plugin(
	{
		appendTransaction(
			transactions: readonly Transaction[],
			oldState: EditorState,
			newState: EditorState
		): Transaction | null {
			let marksToRestore: readonly Mark[] | undefined = undefined;
			let shouldTryToRestoreStoredMarksFromDefault: boolean = !newState.storedMarks;
			let tr: Transaction | null = null;

			// NOTE: This flag indicates creation of the new paragraph.
			// When this happen we should apply marks from the previous context
			// (either stored marks or marks at selection) to the new paragraph
			const shouldSetDefaultFontMarksOnANewParagraph: boolean = transactions.some(
				x => x.getMeta(defaultFontMarksNewParagraphTag)
			);

			// NOTE: this flag indicates that new stored marks have appeared during the transaction
			// (this is NOT a change detection, because during the change marks could disappear)
			// This is needed to cover the case when user removes the last character from the paragraph
			// And we need to preserve marks as default marks on an empty paragraph
			const wereStoredMarksCreatedFromContext: boolean = isNotNil(newState.storedMarks)
				&& !newState.selection
					.eq(oldState.selection);

			const didChangeStoredMarks: boolean = !areMarksEqual(oldState.storedMarks, newState.storedMarks)
				&& newState.selection
					.eq(oldState.selection);

			const shouldSetStoredMarksAsDefaultFontMarks: boolean = wereStoredMarksCreatedFromContext
				|| didChangeStoredMarks;

			if (shouldSetDefaultFontMarksOnANewParagraph) {
				const marksBeforeTransactions: readonly Mark[] = isNotNil(oldState.storedMarks)
					? oldState.storedMarks
					: oldState.selection
						.$to
						.parentOffset !== 0

						? oldState.selection
							.$from
							.marks()
						: [];

				tr = applyDefaultFontMarksToEmptyParagraph(
					newState,
					tr,
					marksBeforeTransactions
				);

				marksToRestore = marksBeforeTransactions;
				shouldTryToRestoreStoredMarksFromDefault = true;
			} else if (shouldSetStoredMarksAsDefaultFontMarks) {
				tr = applyDefaultFontMarksToEmptyParagraph(
					newState,
					tr,
					newState.storedMarks ?? []
				);

				marksToRestore = newState.storedMarks ?? [];
				shouldTryToRestoreStoredMarksFromDefault = true;
			}

			if (shouldTryToRestoreStoredMarksFromDefault) {
				tr = restoreStoredMarksFromEmptyParagraph(
					newState,
					tr,
					marksToRestore
				);
			}

			tr = cleanUpDefaultFontMarks(newState, tr);

			return tr;
		}
	}
);

export function convertMarksToDefaultFontMarks(
	marks: readonly Mark[],
	schema: Schema<never, never>
): SupportedDefaultFontMarks | null {
	let result: SupportedDefaultFontMarks | undefined = undefined;

	const ensureIsDefined = (
		result: SupportedDefaultFontMarks | undefined
	): SupportedDefaultFontMarks => {
		if (!result) {
			return { ...emptyDefaultFontMarks };
		}

		return result;
	};

	for (const mark of marks) {
		if (doesMarkMatchType(mark, "strong", schema)) {
			result = ensureIsDefined(result);
			result.isBold = true;
		}

		if (doesMarkMatchType(mark, "em", schema)) {
			result = ensureIsDefined(result);
			result.isItalic = true;
		}

		if (doesMarkMatchType(mark, "underline", schema)) {
			result = ensureIsDefined(result);
			result.isUnderline = true;
		}

		if (doesMarkMatchType(mark, "strikeThrough", schema)) {
			result = ensureIsDefined(result);
			result.isStrikeThrough = true;
		}

		if (doesMarkMatchType(mark, "fontFamily", schema)) {
			const attrs = mark.attrs as FontFamilyMarkAttributes;

			switch (attrs.kind) {
				case FontFamilyKind.Predefined:
					result = ensureIsDefined(result);
					result.fontFamily = fontFamilyDefinition[attrs.family]
						.textRepresentation;
					break;
				case FontFamilyKind.Custom:
					result = ensureIsDefined(result);
					result.fontFamily = attrs.familyValue;
					break;
				default:
					never(attrs);
			}
		}

		if (doesMarkMatchType(mark, "fontSize", schema)) {
			const attrs = mark.attrs as FontSizeMarkAttributes;

			result = ensureIsDefined(result);
			result.fontSize = attrs.size;
		}

		if (doesMarkMatchType(mark, "color", schema)) {
			const attrs = mark.attrs as ColorMarkAttributes;

			result = ensureIsDefined(result);
			result.color = attrs.color;
		}
	}

	if (!result) {
		return null;
	}

	return result;
}

export const defaultFontMarksNewParagraphTag = new PluginKey("defaultFontMarksNewParagraphTag");

function convertDefaultFontMarksToMarks(
	defaultFontMarks: SupportedDefaultFontMarks,
	schema: Schema<never, never>
): Mark[] {
	const result: Mark[] = [];

	if (defaultFontMarks.isBold && hasMarks(schema, ["strong"])) {
		result.push(
			schema.marks
				.strong
				.create()
		);
	}

	if (defaultFontMarks.isItalic && hasMarks(schema, ["em"])) {
		result.push(
			schema.marks
				.em
				.create()
		);
	}

	if (defaultFontMarks.isUnderline && hasMarks(schema, ["underline"])) {
		result.push(
			schema.marks
				.underline
				.create()
		);
	}

	if (defaultFontMarks.isStrikeThrough && hasMarks(schema, ["strikeThrough"])) {
		result.push(
			schema.marks
				.strikeThrough
				.create()
		);
	}

	if (isNotNilOrEmpty(defaultFontMarks.fontFamily) && hasMarks(schema, ["fontFamily"])) {
		const predefinedFontFamilyResult: TryResult<FontFamily> = tryResolvePredefinedFontFamily(
			defaultFontMarks.fontFamily
		);

		let attrs: FontFamilyMarkAttributes;

		if (predefinedFontFamilyResult.wasSuccessful) {
			attrs = {
				kind: FontFamilyKind.Predefined,
				family: predefinedFontFamilyResult.result
			};
		} else {
			attrs = {
				kind: FontFamilyKind.Custom,
				familyValue: defaultFontMarks.fontFamily
			};
		}

		result.push(
			schema.marks
				.fontFamily
				.create(attrs)
		);
	}

	if (isNotNilOrEmpty(defaultFontMarks.fontSize) && hasMarks(schema, ["fontSize"])) {
		let attrs: FontSizeMarkAttributes;

		if (isPredefinedFontSize(defaultFontMarks.fontSize)) {
			attrs = {
				kind: FontSizeKind.Predefined,
				size: defaultFontMarks.fontSize
			};
		} else {
			attrs = {
				kind: FontSizeKind.Custom,
				size: defaultFontMarks.fontSize
			};
		}

		result.push(
			schema.marks
				.fontSize
				.create(attrs)
		);
	}

	if (isNotNilOrEmpty(defaultFontMarks.color) && hasMarks(schema, ["color"])) {
		const attrs: ColorMarkAttributes = {
			color: defaultFontMarks.color
		};

		result.push(
			schema.marks
				.color
				.create(attrs)
		);
	}

	return result;
}

function applyDefaultFontMarksToEmptyParagraph(
	newState: EditorState,
	tr: Transaction | null,
	marksAtContext: readonly Mark[]
): Transaction | null {
	const selection: Selection = isNotNil(tr)
		? tr.selection
		: newState.selection;

	if (!selection.empty) {
		return tr;
	}

	const {
		$from
	} = selection;

	const {
		parent,
		pos,
		parentOffset
	} = $from;

	if (!parent.isTextblock) {
		return tr;
	}

	if (parent.content
		.size !== 0
	) {
		return tr;
	}

	const defaultFontMarks: SupportedDefaultFontMarks | null = convertMarksToDefaultFontMarks(
		marksAtContext,
		newState.schema
	);

	const parentFrom: number = pos - parentOffset - 1;
	const parentTo: number = parentFrom + parent.nodeSize;

	const existingAttrs: Record<string, any> = parent.attrs;

	if (!tr) {
		tr = newState.tr;
	}

	tr.setBlockType(
		parentFrom,
		parentTo,
		parent.type,
		{
			...existingAttrs,
			defaultFontMarks
		}
	);

	return tr;
}

function restoreStoredMarksFromEmptyParagraph(
	newState: EditorState,
	tr: Transaction | null,
	marksToApply: readonly Mark[] | undefined
): Transaction | null {
	const selection: Selection = isNotNil(tr)
		? tr.selection
		: newState.selection;

	const {
		$from
	} = selection;

	const {
		parent
	} = $from;

	const {
		isTextblock,
		content,
		attrs
	} = parent;

	if (!isTextblock) {
		return tr;
	}

	if (content.size !== 0) {
		return tr;
	}

	let marks: readonly Mark[];

	if (isNotNil(marksToApply)) {
		marks = marksToApply;
	} else {
		if (!isDefaultFontMarksNodeAttributes(attrs)) {
			return tr;
		}

		marks = convertDefaultFontMarksToMarks(
			attrs.defaultFontMarks,
			newState.schema
		);
	}

	if (!tr) {
		tr = newState.tr;
	}

	tr.setStoredMarks([...marks]);

	return tr;
}

function cleanUpDefaultFontMarks(
	newState: EditorState,
	tr: Transaction | null
): Transaction | null {
	const selection: Selection = isNotNil(tr)
		? tr.selection
		: newState.selection;

	const {
		$from,
		empty
	} = selection;

	if (!empty) {
		return tr;
	}

	const {
		parent,
		pos,
		parentOffset
	} = $from;

	if (!parent.isTextblock) {
		return tr;
	}

	if (parent.content.size === 0) {
		return tr;
	}

	const {
		attrs
	} = parent;

	if (!isDefaultFontMarksNodeAttributes(attrs)) {
		return tr;
	}

	const parentFrom: number = pos - parentOffset - 1;
	const parentTo: number = parentFrom + parent.nodeSize;

	const marksToRestore: Mark[] = convertDefaultFontMarksToMarks(
		attrs.defaultFontMarks,
		newState.schema
	);

	if (!tr) {
		tr = newState.tr;
	}

	tr.setBlockType(
		parentFrom,
		parentTo,
		parent.type,
		{
			...attrs,
			defaultFontMarks: undefined
		}
	);

	for (const mark of marksToRestore) {
		tr.addMark(parentFrom, parentTo, mark);
	}

	return tr;
}

function areMarksEqual(
	set1: readonly Mark[] | nil,
	set2: readonly Mark[] | nil
): boolean {
	if (!set1 && !set2) {
		return true;
	}

	if (!set1 || !set2) {
		return false;
	}

	return set1.every(m => m.isInSet(set2))
		&& set1.length === set2.length;
}

function doesMarkMatchType(
	mark: Mark,
	markName: RichTextEditorMarks,
	schema: Schema<never, never>
): boolean {
	if (!hasMarks(schema, [markName])) {
		return false;
	}

	return mark.type === schema.marks[markName];
}